import { KeysOf, tuple } from "functions/FunnyTypes";
import config from "config.json";

type Schema =
"number"
| "string"
| "boolean"
| `lit(${string})`
| readonly [Schema, number]
| readonly [Schema]
| { [k : string]: Schema };

function assert(cond: boolean, expected: any, got: any) : cond is true {
	if (config.DEBUG && !cond) console.warn("Parsing failed", { expected, got });
	return cond;
}


type StoT<S extends Schema>
= S extends "number" ? number
: S extends "string" ? string
: S extends "boolean" ? boolean
: S extends `lit(${infer L})` ? L
: S extends readonly [infer T, infer N] ? T extends Schema ? N extends number ? tuple<StoT<T>, N> : never : never
: S extends readonly [infer T] ? T extends Schema ? StoT<T>[] : never
: S extends { [k : string]: Schema } ?
	{ -readonly [k in keyof(S)]: StoT<S[k]> }
: never;

export type { StoT as SchemaToType };

export function FitsSchema<S extends Schema>(obj: unknown, schema: S) : obj is StoT<S> {
	if (schema === "number") {
		return assert(typeof(obj) === "number", schema, obj);
	} else if (schema === "boolean") {
		return assert(typeof(obj) === "boolean", schema, obj);
	} else if (schema === "string") {
		return assert(typeof(obj) === "string", schema, obj);
	} else if (typeof(schema) === "string") {
		if (schema.startsWith("lit(") && schema.endsWith(")")) {
			const lit = schema.slice(4, schema.length-1);
			return assert(obj === lit, schema, obj);
		} return false;
	} else if (Array.isArray(schema)) {
		if (!Array.isArray(obj)) return assert(false, schema, obj);

		if (schema.length === 2 && obj.length !== schema[1]) return assert(false, schema, obj);

		for (let i = 0; i < schema[1]; i++) {
			if (!FitsSchema(obj[i], schema[0])) return assert(false, obj[i], schema[0]);
		}

		return true;
	} else {
		if (typeof(obj) !== "object" || obj === null) return assert(false, schema, obj);

		for (let k of KeysOf(schema)) {
			if (!FitsSchema(obj[k], schema[k])) {
				if (config.DEBUG) console.warn("At", k);
				return false;
			}
		}

		return true;
	}
}

